import { useEffect, useState, useCallback, Dispatch, SetStateAction, useRef } from 'react';
import { RootState, useSelector } from 'redux/store';
import { Box, Link, Typography } from '@mui/material';
import { CustomDataGrid } from 'components/datagrid/Custom';
import { GridColDef } from '@mui/x-data-grid';
import AddBrandedLinkDialog from './AddBrandedLinkDialog';
import { BrandedLink } from '../../../@types/brandedLink';
import useCustomDispatch from 'redux/dispatch';
import { deleteBrandedLink, getBrandedLinks } from 'redux/slices/brandedLink';
import { DataGridKey } from 'redux/slices/datagrid';
import MoreMenuButton from 'components/util/MoreMenuButton';
import { useTranslator } from 'translation/useTranslator';
import { rules, useAccess } from 'access';

const DATA_GRID_KEY = DataGridKey.BrandedLinksList;

const BrandedLinks = ({
  displayAddBrandedLink,
  setDisplayAddBrandedLink
}: {
  displayAddBrandedLink: boolean;
  setDisplayAddBrandedLink: Dispatch<SetStateAction<boolean>>;
}) => {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const canUpdateBrandedLinks = useAccess(rules.brandedLink.update);
  const canDeleteBrandedLinks = useAccess(rules.brandedLink.delete);
  const { brandedLinks, loading } = useSelector((state: RootState) => state.brandedLink);
  const [brandedLinkToEdit, setBrandedLinkToEdit] = useState<BrandedLink | undefined>();
  const brandedLinkToDeleteRef = useRef<undefined | string>(undefined);
  const [take] = useState(25);

  const fetchBrandedLinks = useCallback(() => {
    customDispatch({ action: getBrandedLinks, disableSuccessMessage: true });
  }, [customDispatch]);

  useEffect(() => {
    fetchBrandedLinks();
  }, [fetchBrandedLinks]);

  const columns: GridColDef[] = [
    {
      field: 'id'
    },
    {
      field: 'name',
      headerName: translator.brandedLinkName(),
      flex: 1
    },
    {
      field: 'url',
      headerName: translator.brandedLinkUrl(),
      flex: 2
    },
    ...(canUpdateBrandedLinks || canDeleteBrandedLinks
      ? [
          {
            field: 'edit',
            headerName: '',
            editable: false,
            disableColumnMenu: true,
            hideable: false,
            align: 'center',
            hideSortIcons: true,
            filterable: false,
            sortable: false,
            renderCell: (cellValues) => {
              const res = cellValues.row as BrandedLink;
              return (
                <MoreMenuButton
                  key={res.id}
                  itemType={translator.brandedLink().toLowerCase()}
                  onEditAccess={rules.brandedLink.update}
                  onEdit={() => {
                    setDisplayAddBrandedLink(true);
                    setBrandedLinkToEdit(res);
                  }}
                  onDeleteAccess={rules.brandedLink.delete}
                  onDelete={() => {
                    if (!brandedLinkToDeleteRef.current) return;
                    customDispatch({
                      action: deleteBrandedLink,
                      actionParameters: brandedLinkToDeleteRef.current,
                      onSuccess: () => {
                        fetchBrandedLinks();
                      }
                    });
                  }}
                  onMenuClick={(opened) => {
                    if (!opened) return;
                    brandedLinkToDeleteRef.current = res.id;
                  }}
                />
              );
            }
          } as GridColDef
        ]
      : [])
  ];

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
        <Link
          href="https://docs.nps.today/guides/BrandedLinks/"
          target="_blank"
          rel="noreferrer"
        >
          {translator.brandedLinkLearnMore()}
        </Link>
      </Typography>
      <CustomDataGrid
        dataGridKey={DATA_GRID_KEY}
        columns={columns}
        rows={brandedLinks}
        loading={loading.brandedLinks}
        disableRowSelectionOnClick
        pagination
        rowCount={brandedLinks.length}
        paginationModel={{ page: 0, pageSize: take }}
        pageSizeOptions={[take]}
      />

      <AddBrandedLinkDialog
        display={displayAddBrandedLink}
        close={() => {
          setDisplayAddBrandedLink(false);
          setBrandedLinkToEdit(undefined);
        }}
        brandedLinkToEdit={brandedLinkToEdit}
        fetchBrandedLinks={fetchBrandedLinks}
      />
    </Box>
  );
};

export default BrandedLinks;
